import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMuteBadges } from "./useMuteBadges";
import { CheckSquare, ChevronUp, StickyNote, X } from "lucide-react";
import { clsx } from "clsx";
import type { DocHandle } from "../sync/docManager";
import { useDocSelector } from "../core/useDocSelector";
import { useNow } from "../core/useNow";
import { applyQuickAdd, quickAddPropDefs } from "../core/ops";
import { parseQuickAdd } from "../core/quickadd";
import { Input } from "../ui";
import { STALE_MS, readTray, trayAdd, trayRemove, type TrayItem } from "./tray";

/**
 * Working-memory tray (spec §9.19): a strip at the bottom of every screen. Ctrl+Shift+A focuses it;
 * Enter parks the thought without creating a task. On a parked item: Enter → task, Shift+Enter → note.
 * Items older than 7 days are flagged for triage, never deleted (D-22). `compact` (narrow and short screens): the
 * input takes the row and the items unfold above it behind a *Tray (n)* button.
 */
export function TrayStrip({ core, compact = false }: { core: DocHandle | null; compact?: boolean }) {
  const { t } = useTranslation();
  const now = useNow();
  const mute = useMuteBadges();
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const items = useDocSelector(core, (doc) => readTray(doc)) ?? [];
  const [unfolded, setUnfolded] = useState(false);
  const showItems = !compact || unfolded;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "a") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!core || core.readOnly) return null;
  const promote = (item: TrayItem, note: boolean) => {
    applyQuickAdd(
      core.doc,
      parseQuickAdd((note ? "> " : "") + item.text, {
        propDefs: quickAddPropDefs(core.doc),
        now: new Date(),
      }),
    );
    trayRemove(core.doc, item.id);
  };

  return (
    <div
      className={clsx(
        "z-20 flex shrink-0 gap-2 border-t border-neutral-200 bg-[var(--surface-2)] py-1.5 dark:border-neutral-800",
        compact ? "flex-col-reverse px-3" : "items-center px-7",
      )}
      data-testid="tray"
      role="region"
      aria-label={t("tray.label")}
    >
      <div className={clsx("flex items-center gap-2", compact ? "w-full" : "w-64 shrink-0")}>
        <form
          className="min-w-0 flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (!text.trim()) return;
            trayAdd(core.doc, text.trim());
            setText("");
          }}
        >
          <Input
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={compact ? t("tray.placeholderShort") : t("tray.placeholder")}
            aria-label={t("tray.placeholderShort")}
            className="h-[28px] py-0 text-[12.5px] touch:h-11"
            data-testid="tray-input"
          />
        </form>
        {compact && (
          <button
            type="button"
            aria-expanded={unfolded}
            aria-controls="tray-items"
            onClick={() => setUnfolded((v) => !v)}
            className="inline-flex h-[28px] shrink-0 items-center gap-1 rounded-sm border border-neutral-200 bg-[var(--surface)] px-2 text-[12.5px] text-neutral-700 aria-expanded:border-accent aria-expanded:text-accent touch:h-11 dark:border-neutral-800 dark:text-neutral-200"
            data-testid="tray-toggle"
          >
            {mute ? t("tray.toggleNoCount") : t("tray.toggle", { n: items.length })}
            <ChevronUp
              size={13}
              aria-hidden
              className={clsx("transition-transform", !unfolded && "rotate-180")}
            />
          </button>
        )}
      </div>
      {showItems && (
        <ul
          id="tray-items"
          className={clsx(
            "flex min-w-0 flex-1 gap-1.5",
            compact ? "max-h-[40dvh] flex-wrap items-start overflow-y-auto" : "items-center overflow-x-auto",
          )}
        >
          {items.map((item) => {
            const stale = now - Date.parse(item.at) > STALE_MS;
            return (
              <li
                key={item.id}
                tabIndex={0}
                data-testid="tray-item"
                data-stale={stale}
                title={t("tray.itemHint")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    promote(item, e.shiftKey);
                  } else if (e.key === "Delete" || e.key === "Backspace") trayRemove(core.doc, item.id);
                }}
                className="flex h-[26px] min-w-0 max-w-full shrink-0 items-center gap-1 rounded-sm border border-neutral-200 bg-[var(--surface)] pl-2 pr-0.5 text-[12.5px] focus-visible:border-accent focus-visible:outline-none dark:border-neutral-800"
              >
                <span className="min-w-0 max-w-[16rem] truncate">{item.text}</span>
                {item.source && item.source !== "app" && (
                  <span className="text-[10.5px] text-neutral-400" data-testid="tray-source">
                    {t(`tray.source.${item.source}`, { defaultValue: item.source })}
                  </span>
                )}
                {stale && <span className="font-mono text-[10px] text-warn">{t("tray.stale")}</span>}
                <button
                  type="button"
                  aria-label={t("tray.toTask")}
                  title={t("tray.toTask")}
                  onClick={() => promote(item, false)}
                  className="grid h-5 w-5 place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  <CheckSquare size={12} aria-hidden />
                </button>
                <button
                  type="button"
                  aria-label={t("tray.toNote")}
                  title={t("tray.toNote")}
                  onClick={() => promote(item, true)}
                  className="grid h-5 w-5 place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  <StickyNote size={12} aria-hidden />
                </button>
                <button
                  type="button"
                  aria-label={t("common.remove")}
                  onClick={() => trayRemove(core.doc, item.id)}
                  className="grid h-5 w-5 place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  <X size={12} aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
